import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import { isMobile } from "react-device-detect";

import { InputGroup } from "react-bootstrap";

import { format } from "date-fns";
import * as XLSX from "xlsx";

import * as Constant from "../../../Constant.js";
import axios from "@app/lib/axios";
import utility from "@app/lib/utility";

import AgGridReactEx from "@app/components/AgGridReactEx";

import BranchSelect from "./BranchSelect.js";

import ModifyContractModal from "./ModifyContractModal.js";
import RecontractStatusViewModal from "./RecontractStatusViewModal.js";

const getInitSearchFilter = (serviceAppList, appAuthTypeList) => {
    const newSearchFilter = {
        all: true,
        noContract: false,
    };

    serviceAppList.forEach((a) => {
        if(a.contractUseTas) {
            newSearchFilter[a.key] = false;
        }
    });
    appAuthTypeList.all.forEach((at) => newSearchFilter[`${at.appId}_${at.type}`] = false);

    return newSearchFilter;
}

const NurseryListHQ = ({ branch_id, branchList, query, selectedBranch, detailFunc, seasonInfo, serviceAppList, appAuthTypeList, selectedAppContractType = false, summaryLoadCompleted }) => {
    const history = useHistory();
    const location = useLocation();
    const [loadingData, setLoadingData] = useState(false);
    const [defaultSearchParamValue] = useState(location.state && location.state.searchParam ? location.state.searchParam.value || "" : "");
    const [branchId, setBranchId] = useState(branch_id);
    const [searchType, setSearchType] = useState("and");
    const [searchParam, setSearchParam] = useState(
        location.state && location.state.searchParam
            ? location.state.searchParam
            : {
                  page: 0,
                  type: "",
                  value: "",
                  sort_col: "update_date",
                  sort: "desc",
                  recontract: "",
                  operand: "and",
                  nocontractDisabled: false,
              }
    );
    const [searchFilter, setSearchFilter] = useState(
        location.state && location.state.searchFilter
            ? location.state.searchFilter
            : getInitSearchFilter(serviceAppList, appAuthTypeList)
    );
    const [data, setData] = useState(
        location.state && location.state.data
            ? location.state && location.state.data
            : {
                  loaded: false,
                  bizApp: [],
                  total: 0,
                  list: [],
                  maxPage: 0,
              }
    );
    const [tableData, setTableData] = useState(false);
    const [recontractStatusViewModalData, setRecontractStatusViewModalData] = useState({
        nursery_id: 0,
        contractList: [],
        nurseryName: "",
        directorName: "",
        address: "",
    });
    const tableRef = useRef();
    const searchInputRef = useRef();
    useEffect(() => {
        if (location.state) {
            location.state = null;
        }
        if (!loadingData && summaryLoadCompleted && branchList) {
            getData(searchParam.page, branchId, data.loaded);
        }
        onSearch();
        return () => {};
    }, [branch_id, query, selectedAppContractType, branchList, summaryLoadCompleted]);
    useEffect(() => {
        if (data && data.list.length > 0) {
            applyData(searchParam, data.list);
        }
        return () => {};
    }, [selectedBranch]);
    const getData = (page, bid, loaded = false) => {
        if (!loaded) {
            setLoadingData(true);
            axios({
                method: "get",
                url: `${Constant.API_DOMAIN}/api/nursery/list/${branch_id > 0 ? "RA" : "A"}/${bid}`,
            })
                .then((res) => {
                    const result = res.data.result;
                    const _data = { ...data };
                    const bizApp = {};
                    if (res.data.bizApp) {
                        res.data.bizApp.forEach((app) => {
                            bizApp[app] = { id: app, hasPreContractedBranch: false };
                            bizApp[app].info = serviceAppList.find((sa) => sa.key == app);
                            bizApp[app].authTypeList = appAuthTypeList[app];
                        });
                    }
                    _data.bizApp = bizApp || [];
                    _data.list = result.list || [];
                    _data.total = result.list && result.list.length > 0 ? result.list.length : 0;
                    if (branch_id === 0 && branchList) {
                        _data.list.forEach((s) => {
                            const contractBranchList = [];
                            let contractBranchListTooltip = "";
                            serviceAppList.forEach((a) => {
                                if(a.contractUseTas && s[`${a.key}_branch`]) {
                                    const b = branchList.find((b) => b.id == s[`${a.key}_branch`]);
                                    if (b) {
                                        const cb = contractBranchList.find((cb) => cb.id == b.id);
                                        if(cb) {
                                            cb.app.push(a);
                                        } else {
                                            contractBranchList.push({id: b.id, name: b.name, app: [{id: a.key, name: a.name}]});
                                        }
                                    }
                                }
                            });
                            s.branchList = contractBranchList;
                            if(contractBranchList.length > 0) {
                                contractBranchListTooltip = contractBranchList.reduce((acc, c) => acc + (acc ? ' / ' : '') + `${c.name}: ${c.app.reduce((accApp, ca) => accApp + (accApp ? ',' : '') + ca.name, '')}`, '');
                            }
                            s.branchFull = contractBranchListTooltip;
                        });
                    }
                    _data.loaded = true;
                    setData(_data);
                    applyData(searchParam, _data.list);
                    setLoadingData(false);
                })
                .catch((error) => {
                    console.log(error);
                    setLoadingData(false);
                    console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
                });
        } else {
            applyData(searchParam, data.list);
            setLoadingData(false);
        }
    };

    const listColumn = [
        {
            headerName: "No",
            headerClass: "bg-dark text-light",
            field: "id",
            sortable: false,
            width: 80,
            minWidth: 50,
            resizable: true,
            cellClass: "text-center",
            filter: false,
            valueGetter: "node.rowIndex + 1",
        },
        {
            headerName: "원명",
            headerClass: "bg-dark text-light",
            cellClass: "text-left",
            field: "name",
            sortable: true,
            minWidth: 120,
            resizable: true,
            filter: false,
        },
        {
            headerName: "지역",
            headerClass: "bg-dark text-light",
            cellClass: "text-left",
            field: "address",
            sortable: true,
            minWidth: 120,
            resizable: true,
            filter: false,
            hide: isMobile,
            cellRenderer: (param) => {
                if (param.data.status != "active") return "";
                return param.value;
            },
        },
        {
            headerName: "원장명",
            headerClass: "bg-dark text-light",
            field: "director_name",
            sortable: true,
            width: 110,
            minWidth: 100,
            resizable: true,
            cellClass: "text-center",
            filter: false,
            hide: isMobile,
            cellRenderer: (param) => {
                if (param.data.status != "active") return "";
                return param.value;
            },
        },
        {
            headerName: "원 코드",
            headerClass: "bg-dark text-light",
            field: "code",
            sortable: true,
            width: 100,
            minWidth: 80,
            resizable: true,
            cellClass: "text-center",
            filter: false,
            hide: true,
            cellRenderer: (param) => {
                if (param.data.status != "active") return '<span class="text-danger">등록 대기</span>';
                return param.value;
            },
        },
        {
            headerName: "원 타입",
            headerClass: "bg-dark text-light",
            field: "service_type",
            sortable: true,
            width: 70,
            minWidth: 70,
            resizable: true,
            cellClass: "text-center",
            filter: false,
            hide: true,
            cellRenderer: (param) => {
                if (param.value == "N") {
                    return '<span class="text-success">일반</span>';
                } else if (param.value == "B") {
                    return '<span class="text-primary">영업용</span>';
                } else {
                    return '<span class="text-muted">테스트</span>';
                }
            },
        },
        {
            headerName: "재계약",
            headerClass: "bg-dark text-light",
            field: "reContract",
            sortable: true,
            minWidth: 80,
            width: 80,
            resizable: true,
            hide: !(seasonInfo && seasonInfo.isRecontractSeason),
            cellClass: "text-center",
            filter: false,
            cellRenderer: (param) => {
                let needRecontract = 0;
                let totalRecontract = 0;
                serviceAppList.forEach((a) => {
                    if(a.contractUseTas) {
                        needRecontract += (param.data[`${a.key}_recontract`] || 0);
                        totalRecontract += (param.data[`${a.key}_recontract_total`] || 0);
                    }
                });
                if (needRecontract > 0) return `<button class='btn btn-danger btn-rounded font-12' style='padding: 2px 6px; width: 55px'>재계약</button>`;
                if (totalRecontract > 0 && needRecontract === 0) return `<button class='btn btn-success btn-rounded font-12' style='padding: 2px 6px; width: 55px'>완&nbsp;료</button>`;
                return "";
            },
        },
        {
            headerName: "계약 지사",
            headerClass: "bg-dark text-light",
            field: "branch",
            tooltipField: "branchFull",
            sortable: true,
            minWidth: 100,
            resizable: true,
            hide: !(branch_id === 0),
            cellClass: "text-left",
            filter: false,
            cellRenderer: (param) => {
                if (branch_id === 0 && param.data.branchList.length > 0) {
                    return param.data.branchList[0].name + (param.data.branchList.length > 1 ? " <span style='color: #B85038'>외</span>" : "");
                }
                return "-";
            },
        },
        {
            headerName: "승인 대기",
            headerClass: "bg-waiting",
            field: "wt",
            sortable: true,
            width: 80,
            resizable: true,
            cellClass: "text-center bg-waiting-90",
            filter: false,
            cellRenderer: (param) => {
                if (param.data.status != "active") return "-";
                let hasWaiting = 0;
                serviceAppList.forEach((a) => {
                    if(a.contractUseTas) {
                        hasWaiting += (param.data[`${a.key}_wt`] || 0);
                    }
                });

                return hasWaiting;
            },
        },
    ];
    const columnUnionList = [];
    serviceAppList.forEach((a) => {
        if(a.contractUseTas) {
            listColumn.push({
                headerName: a.name,
                headerClass: "bg-app",
                field: `${a.key}_ap`,
                sortable: true,
                width: 80,
                resizable: true,
                cellClass: "text-center bg-app-70",
                filter: false,
                cellRenderer: (param) => {
                    if (param.data[`${a.key}_ap`] === undefined) return "-";
                    return param.value || 0;
                },
            });
            if(appAuthTypeList[a.key]) {
                appAuthTypeList[a.key].forEach((at) => {
                    columnUnionList.push({
                        headerName: at.name,
                        headerClass: "bg-union",
                        field: `${at.appId}_${at.type}_ap`,
                        sortable: true,
                        width: 80,
                        resizable: true,
                        cellClass: "text-center bg-union-70",
                        filter: false,
                        cellRenderer: (param) => {
                            if (param.data[`${at.appId}_${at.type}_ap`] === undefined) return "-";
                            return param.value || 0;
                        },
                    });
                });
            }
        }
    });
    columnUnionList.forEach((c) => listColumn.push(c));
    listColumn.push({
        headerName: "수정일",
        headerClass: "bg-dark text-light",
        field: "update_date",
        sortable: true,
        minWidth: 100,
        width: 100,
        resizable: true,
        cellClass: "text-center",
        filter: false,
        cellRenderer: (param) => {
            if (param.value) return param.value.substring(0, 10);
            return "-";
        },
    });

    let gridApi = null;
    const onGridReady = (params) => {
        gridApi = params.api;
        const sortModel = [{ colId: searchParam.sort_col, sort: searchParam.sort }];
        gridApi.setSortModel(sortModel);
    };

    const onSortChanged = (params) => {
        const sortState = params.api.getSortModel();
        if (sortState.length != 0) {
            let searchParamNew = { ...searchParam };
            searchParamNew.sort_col = sortState[0].colId;
            searchParamNew.sort = sortState[0].sort;
            setSearchParam(searchParamNew);
        }
    };
    const onRowClicked = (params) => {
        if (params.api && params.api.getFocusedCell() && params.api.getFocusedCell().column && params.api.getFocusedCell().column.getColId() === "reContract") {
            let needRecontract = 0;
            let totalRecontract = 0;
            serviceAppList.forEach((a) => {
                if(a.contractUseTas) {
                    needRecontract += (params.data[`${a.key}_recontract`] || 0);
                    totalRecontract += (params.data[`${a.key}_recontract_total`] || 0);
                }
            });
            
            if (totalRecontract > 0 && needRecontract === 0) {
                openRecontractStatusViewModal(params.data.name, params.data.id, params.data.director_name, params.data.address);
            }
        } else {
            return onDetail(params.data.id);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            onSearch();
        }
    };
    const onSearch = () => {
        if (searchInputRef && searchInputRef.current) {
            searchParam.value = searchInputRef.current.getValue();
        }
        applyData(searchParam, data.list);
    };

    const applyData = (searchParam, list) => {
        var tableData = listSearch(searchParam, list);

        setTableData(tableData);
        setSearchParam(searchParam);
    };

    const onDetail = (nurseryId, reserveData = true) => {
        searchParam.page = tableRef.current ? tableRef.current.getCurrentPage() : 0;
        const historyState = {
            branchId: branchId,
            branches: branchList,
            data: reserveData ? data : null,
            searchParam: searchParam,
            searchFilter: { ...searchFilter },
        };
        history.replace(location.pathname, historyState);
        detailFunc(nurseryId);
    };

    const onTableDataChanged = () => {};

    const searchField = ["name", "address", "director_name", "code"];
    const onChangeFilter = (e) => {
        const param = { ...searchParam };
        const newSearchFilter = { ...searchFilter };
        if(e.target.name == 'select-operand') {
            param.operand = e.target.value;
            setSearchParam(param);
        } else {
            if ( e.target.value == "all") {
                Object.keys(newSearchFilter).forEach((key) => (newSearchFilter[key] = false));
                newSearchFilter["all"] = true;
            } else {
                newSearchFilter["all"] = false;
                newSearchFilter["noContract"] = false;
                if(e.target.value == "noContract") {
                    // 미사용 원 선택한 경우 다른 필터 제거
                    Object.keys(newSearchFilter).forEach((key) => (newSearchFilter[key] = false));
                    newSearchFilter[e.target.value] = e.target.checked;
                    if (!e.target.checked) {
                        newSearchFilter["all"] = true;
                    }
                } else {
                    newSearchFilter[e.target.value] = e.target.checked;
                    if (!e.target.checked) {
                        let uncheckedAll = true;
                        Object.keys(newSearchFilter).forEach((key) => {
                            if (key != "all" && uncheckedAll && newSearchFilter[key] === true) {
                                uncheckedAll = false;
                            }
                        });
                        if (uncheckedAll) {
                            newSearchFilter["all"] = true;
                        }
                    }
                }
            }
        }
        setSearchFilter(newSearchFilter);
    };
    const onChangeFilterRecontract = (e) => {
        const param = { ...searchParam };
        if (!param.recontract) {
            const newSearchFilter = {
                all: false,
                noContract: false,
            };

            serviceAppList.forEach((a) => {
                if(a.contractUseTas) {
                    newSearchFilter[a.key] = true;
                }
            });
            appAuthTypeList.all.forEach((at) => newSearchFilter[`${at.appId}_${at.type}`] = false);
            setSearchFilter(newSearchFilter);
        }
        param.recontract = e.target.value;
        if (param.recontract) {
            param.noContract = false;
            param.nocontractDisabled = true;
        } else {
            param.nocontractDisabled = false;
        }
        setSearchParam(param);
        applyData(param, data.list);
    };
    const listSearch = (searchParam, list) => {
        let localList = list;
        // 지사 구분
        if(selectedBranch > 0) {
            let listNew = [];
            localList.forEach((item, idx) => {
                if(item.branchList && item.branchList.find((b) => b.id == selectedBranch)) {
                    listNew.push(item);
                }
            });
            localList = listNew;
        }
        // 앱 구분
        if (selectedAppContractType && selectedAppContractType.app_id && selectedAppContractType.contract_type) {
            let listNew = [];
            localList.forEach((item, idx) => {
                if (selectedAppContractType.contract_type == "all" && (item[`${selectedAppContractType.app_id}_new`] > 0 || item[`${selectedAppContractType.app_id}_renew`] > 0)) {
                    if (listNew.findIndex((d) => d.id == item.id) == -1) {
                        listNew.push(item);
                    }
                } else if (item[`${selectedAppContractType.app_id}_${selectedAppContractType.contract_type}`] > 0) {
                    if (listNew.findIndex((d) => d.id == item.id) == -1) {
                        listNew.push(item);
                    }
                }
            });
            localList = listNew;
        }
        // 재계약 filter 적용
        if (searchParam.recontract) {
            let listNew = [];
            let searchFilterChecked = [];
            if (searchFilter.all) {
                searchFilterChecked = serviceAppList.filter((sa) => sa.contractUseTas == true).map((sa) => sa.appId);
            } else {
                Object.keys(searchFilter).forEach((key) => {
                    if (key != "all" && key != "noContract" && searchFilter[key] == true) {
                        searchFilterChecked.push(key);
                    }
                });
            }
            if (searchParam.recontract === "re") {
                localList.forEach((item, idx) => {
                    let found = searchParam.operand == 'or' ? false : true;
                    searchFilterChecked.forEach((key) => {
                        found = searchParam.operand == 'or' ? (found || item[`${key}_recontract`] > 0) : (found && item[`${key}_recontract`] > 0);
                    });
                    if(found) {
                        listNew.push(item);
                    }
                });
            } else if (searchParam.recontract === "complete") {
                localList.forEach((item, idx) => {
                    let found = searchParam.operand == 'or' ? false : true;
                    searchFilterChecked.forEach((key) => {
                        const needRecontract = item[`${key}_recontract`] || 0;
                        const totalRecontract = item[`${key}_recontract_total`] || 0;
                        found = searchParam.operand == 'or' ? (found || (totalRecontract > 0 && needRecontract === 0)) : (found && (totalRecontract > 0 && needRecontract === 0));
                    });
                    
                    if(found) {
                        listNew.push(item);
                    }
                });
            } else if (searchParam.recontract === "new") {
                localList.forEach((item, idx) => {
                    let found = searchParam.operand == 'or' ? false : true;
                    searchFilterChecked.forEach((key) => {
                        found = searchParam.operand == 'or' ? (found || (item[`${key}_new`] || 0) > 0) : (found && (item[`${key}_new`] || 0) > 0);
                    });
                    if(found) {
                        listNew.push(item);
                    }
                });
            }
            localList = listNew;
        } else {
            // filter 적용
            if (searchFilter && searchFilter.all == false) {
                let listNew = [];
                let searchFilterChecked = [];
                Object.keys(searchFilter).forEach((key) => {
                    if (key != "all" && searchFilter[key] == true) {
                        searchFilterChecked.push(key);
                    }
                });
                localList.forEach((item, idx) => {
                    let found = searchParam.operand == 'or' ? false : true;
                    if(searchFilter.noContract) {
                        // 미사용 원인 경우 and or 없음
                        Object.keys(searchFilter).forEach((key) => {
                            if (key != "all" && key != "noContract") {
                                found = found && !(item[`${key}_ap`] > 0);
                            }
                        });
                    } else {
                        searchFilterChecked.forEach((key) => {
                            found = searchParam.operand == 'or' ? (found || item[`${key}_ap`] > 0) : (found && item[`${key}_ap`] > 0);
                        });
                    }
                    if(found) {
                        listNew.push(item);
                    }
                });
                localList = listNew;
            }
        }
        let tableDataNew = [];
        if (!searchParam.value) {
            tableDataNew = localList;
        } else {
            localList.forEach((item) => {
                searchField.forEach((field) => {
                    if (searchParam.type && item.type !== searchParam.type) {
                        return;
                    }
                    if (item[field] && item[field].indexOf(searchParam.value) >= 0) {
                        if (tableDataNew.findIndex((d) => d.id == item.id) == -1) {
                            tableDataNew.push(item);
                        }
                    }
                });
            });
        }
        return tableDataNew;
    };

    const openRecontractStatusViewModal = (nurseryName, id, directorName, address) => {
        setRecontractStatusViewModalData({
            nursery_id: id,
            nurseryName: nurseryName,
            directorName: directorName,
            address: address,
            contractList: [],
            bizApp: null,
        });
        axios({
            method: "get",
            url: `${Constant.API_DOMAIN}/api/contract/list/${branchId}/${id}`,
        })
            .then((res) => {
                const bizApp = {};
                const list = res.data && res.data.contract ? res.data.contract : [];
                if (res.data.bizApp) {
                    res.data.bizApp.forEach((app) => {
                        bizApp[app] = { id: app, hasPreContractedBranch: false };
                        bizApp[app].info = serviceAppList.find((sa) => sa.key == app);
                        bizApp[app].authTypeList = appAuthTypeList[app];
                    });
                }
                if (res.data.nurseryAppBranch && res.data.nurseryAppBranch.length > 0) {
                    res.data.nurseryAppBranch.forEach((appContractBranch) => {
                        if (appContractBranch.app_id != "homebooks" && bizApp[appContractBranch.app_id]) {
                            if (!bizApp[appContractBranch.app_id].hasPreContractedBranch) {
                                bizApp[appContractBranch.app_id].hasPreContractedBranch = appContractBranch.branch_id != branchId;
                            }
                        }
                    });
                }
                // 기존 계약 승인 정보 보관
                list.forEach((c) => {
                    if (c.app_auth) {
                        let allNA = true;
                        serviceAppList.forEach((a) => {
                            if(a.contractUseTas && bizApp[a.key]) {
                                if(c.app_auth[a.key]) {
                                    c.app_auth[a.key].approval_org = c.app_auth[a.key].approval;
                                    c.app_auth[a.key].authType_org = c.app_auth[a.key].authType;
                                    c.app_auth[a.key].authTypeName_org = c.app_auth[a.key].authTypeName;
                                    // 해지된 계약 경우 연령 초기화
                                    if (c.app_auth[a.key].approval == "NA") {
                                        c[bizApp[a.key].info.age.field] = c.age;
                                        // 단독 수량 사용 시 수량 초기화
                                        if(bizApp[a.key].info.hasCellQuantity) {
                                            bizApp[a.key].info.quantity.field.forEach((q) => c[q] = 0);
                                        }
                                    } else {
                                        // 공통 수량 사용여부 체크
                                        if(!bizApp[a.key].info.hasCellQuantity) {
                                            allNA = false;
                                        }
                                    }
                                }
                            }
                        });
                        // 공통 수량 사용하는 서비스 모두 해지된 경우 수량 초기화
                        if (allNA) {
                            bizApp.tomonote.info.quantity.field.forEach((q) => c[q] = 0);
                        }
                    }
                });
                const modifyData = {
                    nursery_id: id,
                    nurseryName: nurseryName,
                    directorName: directorName,
                    address: address,
                    contractList: list,
                    bizApp: bizApp,
                };
                setRecontractStatusViewModalData(modifyData);
            })
            .catch((error) => {
                console.log(error);
                console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
            });
    };
    useEffect(() => {
        if (!loadingData) {
            onSearch();
        }
        return () => {};
    }, [searchFilter]);

    const saveContractTableXlsx = () => {
        if (!tableData || tableData.length === 0) {
            alert("원 목록이 없습니다.");
            return false;
        }

        const excelHandler = {
            getExcelData() {
                const data = tableData
                ? tableData.map((s, idx) => {
                    const row = [
                      idx + 1,
                      s.name,
                      s.type == 'A' ? '어린이집' : '유치원',
                      s.sub_type,
                      s.address,
                      s.tel,
                      s.director_name,
                      s.branchList ? s.branchList.map((b) => b.name).join(',') : '-'
                    ];
                    var iii = '';
                    serviceAppList.forEach((a) => {
                        if(a.contractUseTas) {
                            row.push(s[`${a.key}_ap`], s[`${a.key}_renew`], s[`${a.key}_new`], s[`${a.key}_next`]);
                        }
                    });
                    columnTitleUnion.forEach((at) => {
                        row.push(s[`${at.key}_ap`], s[`${at.key}_renew`], s[`${at.key}_new`], s[`${at.key}_next`]);
                    });
                    row.push(s.create_date);
                    return row;
                })
                : [];
                const excelData = {
                    fileName: `원목록` + "_" + format(new Date(), "yyyyMMddHHmmss") + ".xlsx",
                    sheets: [
                        {
                            name: "원목록",
                            columnTitle: columnTitle,
                            data: data,
                        },
                    ],
                };
                return excelData;
            },
        };
        const nowYear = new Date().getFullYear();
        const columnTitle = [["no","원명","구분","유형","지역","전화번호","원장명","지사"], []];
        const columnTitleUnion = [];
        serviceAppList.forEach((a) => {
            if(a.contractUseTas) {
                for(let i = 0; i < 4; i++) {
                    columnTitle[0].push(a.name);
                }
                columnTitle[1].push("전체",
                        `${nowYear}재계약`,
                        `${nowYear}신규`,
                        `${nowYear + 1}신규`);
                if(appAuthTypeList[a.key]) {
                    appAuthTypeList[a.key].forEach((at) => {
                        columnTitleUnion.push({key: `${at.appId}_${at.type}`, name: at.name});
                    })
                }
            }
        });
        columnTitleUnion.forEach((c) => {
            for(let i = 0; i < 4; i++) {
                columnTitle[0].push(c.name);
            }
            columnTitle[1].push("전체",
                    `${nowYear}재계약`,
                    `${nowYear}신규`,
                    `${nowYear + 1}신규`);
        });
        columnTitle[0].push("등록일");

        var wb = XLSX.utils.book_new();
        var excelData = excelHandler.getExcelData();
        excelData.sheets.forEach((s) => {
            var ws = XLSX.utils.aoa_to_sheet([]);
            const mergeCols  = [];
            ws["!merges"] = [
                { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // no
                { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // name
                { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // type
                { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // sub_type
                { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }, // addr
                { s: { r: 0, c: 5 }, e: { r: 1, c: 5 } }, // tel
                { s: { r: 0, c: 6 }, e: { r: 1, c: 6 } }, // director
                { s: { r: 0, c: 7 }, e: { r: 1, c: 7 } }, // code
            ];
            let mergeColStart = 8;
            serviceAppList.forEach((a) => {
                if(a.contractUseTas) {
                    mergeCols.push({s: {r: 0, c: mergeColStart}, e: {r: 0, c: mergeColStart + 3}});
                    mergeColStart += 4;
                }
            });
            columnTitleUnion.forEach((at) => {
                mergeCols.push({s: {r: 0, c: mergeColStart}, e: {r: 0, c: mergeColStart + 3}});
                mergeColStart += 4;
            });
            mergeCols.push({s: {r: 0, c: mergeColStart}, e: {r: 1, c: mergeColStart}});

            ws["!merges"] = mergeCols;

            XLSX.utils.sheet_add_aoa(ws, [excelData.sheets[0].columnTitle[0]], { origin: { r: 0, c: 0 } });
            XLSX.utils.sheet_add_aoa(ws, [excelData.sheets[0].columnTitle[1]], { origin: { r: 1, c: 8 } });
            XLSX.utils.sheet_add_json(ws, s.data, { origin: { r: 2, c: 0 }, skipHeader: true });

            XLSX.utils.book_append_sheet(wb, ws, s.name);
        });

        var wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });
        utility.download2Excel(wbout, excelData.fileName);
    };

    if(!searchFilter || !listColumn) {
        return <></>;
    }

    return (
        <>
            <div className="row mb-2 align-items-end">
                <div className="col-xl-1 col-lg-1 col-md-2 col-sm-12 col-12 pl-4">
                    <h5 className="mb-0" style={{ alignSelf: "end" }}>
                        {loadingData && <span className="fa fa-spinner"></span>}
                        {!loadingData && (
                            <span>
                                {tableData ? tableData.length.toLocaleString() : "0"} / {data && data.total ? data.total.toLocaleString() : "0"}
                            </span>
                        )}
                    </h5>
                </div>
                <div className="col-xl-9 col-lg-9 col-md-5 col-sm-12 col-12 text-right">
                    <div className="mr-2" style={{ display: "inline" }}>
                        <select
                            className="form-control input-group-append ml-1"
                            name="select-operand"
                            title="검색 조건 선택"
                            style={{ height: "auto", maxWidth: "100px", display: "inline" }}
                            value={searchParam.operand}
                            onChange={onChangeFilter}
                        >
                            <option value="and">AND</option>
                            <option value="or">OR</option>
                        </select>
                    </div>
                    <span className="">
                        <input style={{ display: "inline" }} id="filterAll" type="radio" checked={searchFilter.all} onChange={(e) => onChangeFilter(e)} value="all" />
                        <label htmlFor="filterAll" className="ml-1">
                            전체
                        </label>
                    </span>
                    {seasonInfo && seasonInfo.isRecontractSeason && (
                        <div className="ml-2" style={{ display: "inline" }}>
                            <span></span>
                            <select
                                className="form-control input-group-append ml-1"
                                title="검색 항목 선택"
                                style={{ height: "auto", maxWidth: "100px", display: "inline" }}
                                value={searchParam.recontract}
                                onChange={onChangeFilterRecontract}
                            >
                                <option value="" defaultChecked style={{ color: "#bbbbbb" }}>
                                    전체
                                </option>
                                <option value="re">재계약 대상</option>
                                <option value="complete">재계약 완료</option>
                                <option value="new">{seasonInfo.recontractYear} 신규</option>
                            </select>
                        </div>
                    )}
                    {
                        serviceAppList.map((a) => {
                            if(a.contractUseTas) {
                                return (
                                    <span className="ml-2">
                                        <input id={`filter${a.key}`} type="checkbox" checked={searchFilter[a.key]} onChange={(e) => onChangeFilter(e)} value={a.key} />
                                        <label htmlFor={`filter${a.key}`} className="ml-0">
                                            {a.name}
                                        </label>
                                    </span>
                                );
                            }
                        })
                    }
                    {
                        appAuthTypeList.all.map((a) => {
                            return (
                                <span className="ml-2">
                                    <input id={`filter${a.appId}${a.type}`} type="checkbox" checked={searchFilter[`${a.appId}_${a.type}`]} onChange={(e) => onChangeFilter(e)} value={`${a.appId}_${a.type}`} />
                                    <label htmlFor={`filter${a.appId}${a.type}`} className="ml-1">
                                        {a.name}
                                    </label>
                                </span>
                            );
                        })
                    }
                    <span className="ml-2">
                        <input id="filterNoUse" type="checkbox" checked={searchFilter.noContract} onChange={(e) => onChangeFilter(e)} value="noContract" disabled={searchParam.nocontractDisabled} />
                        <label htmlFor="filterNoUse" className="ml-1">
                            미사용 원
                        </label>
                    </span>
                </div>
                <div className="col-xl-2 col-lg-2 col-md-5 col-sm-12 col-12 text-right">
                    <InputGroup className="input-group my-group">
                        <SearchField handleKeyPress={handleKeyPress} defaultValue={defaultSearchParamValue} ref={searchInputRef} />
                        <InputGroup.Append>
                            <input type="submit" className="btn btn-primary" value="검색" onClick={onSearch} />
                        </InputGroup.Append>
                    </InputGroup>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <AgGridReactEx
                        ref={tableRef}
                        tooltipShowDelay={0}
                        columnDefs={listColumn}
                        rowData={tableData}
                        rowClassRules={{
                            "no-contract-nursery": (params) => {
                                let hasApproval = 0;
                                serviceAppList.forEach((a) => {
                                    if(a.contractUseTas) {
                                        hasApproval += ((params.data[`${a.key}_ap`] || 0) + (params.data[`${a.key}_wt`] || 0));
                                    }
                                });
                                return hasApproval === 0;
                            },
                        }}
                        onRowDataChanged={onTableDataChanged}
                        onRowClicked={onRowClicked}
                        rowSelection="single"
                        pagination={true}
                        onGridReady={onGridReady}
                        onSortChanged={onSortChanged}
                        overlayNoRowsTemplate={`<span>원 목록이 없습니다.</span>`}
                        suppressSizeColumnsToFit={isMobile}
                        excelDownload={true}
                        excelDataHandler={saveContractTableXlsx}
                    />
                </div>
            </div>
                <RecontractStatusViewModal
                    branchId={branchId}
                    nurseryId={recontractStatusViewModalData.nursery_id}
                    nurseryName={recontractStatusViewModalData.nurseryName}
                    bizApp={recontractStatusViewModalData.bizApp}
                    serviceAppList={serviceAppList}
                    list={recontractStatusViewModalData.contractList}
                    seasonInfo={seasonInfo}
                    directorName={recontractStatusViewModalData.directorName}
                    address={recontractStatusViewModalData.address}
                    showModal={recontractStatusViewModalData.contractList.length > 0 && recontractStatusViewModalData.nursery_id > 0 && recontractStatusViewModalData.nurseryName != ""}
                />
        </>
    );
};
const SearchField = forwardRef(({ defaultValue = "", handleKeyPress }, ref) => {
    const [value, setValue] = useState(defaultValue);
    useEffect(() => {
        return () => {};
    }, [defaultValue]);
    useImperativeHandle(ref, () => ({
        getValue() {
            return value;
        },
    }));

    return (
        <>
            <input
                className="form-control"
                type="text"
                value={value}
                placeholder="원명|지역|원장명|원코드"
                onChange={(e) => {
                    setValue(e.target.value);
                }}
                onKeyPress={(e) => handleKeyPress(e)}
            />
        </>
    );
});

export default NurseryListHQ;
